'use client';

import { SliceZone } from '@prismicio/react';
import classNames from 'classnames';
import React from 'react';
import { components } from 'slices';
import styles from './comparison-table.module.scss';
import { motion, useAnimation } from 'framer-motion';
import { ColumnSelectButtons } from './ColumnSelectButtons';
import { Button } from '@get-mosh/msh-shared';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faChevronUp } from '@fortawesome/pro-solid-svg-icons';
import { ComparisonTableRowSlice } from 'prismicio-types';

type ComparisonTableProps = {
    slices: Array<ComparisonTableRowSlice>;
    uid: string;
};

export const ComparisonTable = ({ slices, uid }: ComparisonTableProps) => {
    const [isExpanded, setIsExpanded] = React.useState<boolean>(false);
    const [colVisibleOnMobile, setColVisibleOnMobile] = React.useState<2 | 3>(
        2,
    );
    const animationControls = useAnimation();

    const headerRow =
        slices.find((slice) => slice.primary.is_header_row) ??
        slices.slice(0, 1)[0];
    const tableColCount = headerRow?.items.length;

    const toggleColumn = async () => {
        await animationControls.start({
            opacity: 0,
            transition: { duration: 0.2, delay: 0 },
        });
        setColVisibleOnMobile((prevState) => (prevState === 2 ? 3 : 2));
        await animationControls.start({
            opacity: 1,
            transition: { duration: 0.2, delay: 0 },
        });
        animationControls.stop();
    };

    return (
        <div className="flex flex-col">
            {/* buttons for switching between cols on mobile */}
            <ColumnSelectButtons
                colVisibleOnMobile={colVisibleOnMobile}
                tableColCount={tableColCount}
                headerRow={headerRow}
                toggleColumn={toggleColumn}
            />
            <motion.div
                role="table"
                className="container w-full"
                animate={animationControls}
            // initial={{ opacity: 0 }}
            // transition={{ delay: 0, duration: 1 }}
            >
                {/* Table header row */}
                {headerRow && (
                    <div role="rowgroup">
                        <SliceZone
                            slices={[headerRow]}
                            components={components}
                            context={{
                                uid: uid,
                                header: true,
                                tableColCount,
                                colVisibleOnMobile,
                            }}
                        />
                    </div>
                )}

                {/* Table rows */}
                <div role="rowgroup">
                    <SliceZone
                        slices={slices.filter(
                            (slice) =>
                                !slice.primary.is_header_row &&
                                !slice.primary.hide_row,
                        )}
                        components={components}
                        context={{
                            uid: uid,
                            header: false,
                            tableColCount,
                            colVisibleOnMobile,
                        }}
                    />
                </div>

                {/* Expandable table rows */}
                <div
                    className={classNames(
                        isExpanded
                            ? 'max-h- open-animation max-h-[62rem]'
                            : 'max-h-[0rem]',
                        styles['hidden-rows'],
                    )}
                    role="rowgroup"
                    aria-hidden={!isExpanded}
                    id="hidden-rows"
                >
                    <SliceZone
                        slices={slices
                            .slice(1)
                            .filter((slice) => slice.primary.hide_row)}
                        components={components}
                        context={{
                            uid: uid,
                            header: false,
                            tableColCount,
                            colVisibleOnMobile,
                        }}
                    />
                </div>
            </motion.div>

            {/* Button to show/hide expandable table rows */}
            <Button
                onClick={() => setIsExpanded((state) => !state)}
                variant="tertiary"
                className={styles['expand-button']}
                aria-expanded={isExpanded}
                aria-controls="hidden-rows"
            >
                {isExpanded ? 'View Less' : 'View More'}
                <FontAwesomeIcon
                    icon={faChevronUp}
                    className={classNames(
                        'ml-2 transition-all',
                        isExpanded ? '' : 'rotate-180',
                    )}
                />
            </Button>
        </div>
    );
};
